/**
 * Workspace Hook
 *
 * Provides workspace state and actions for components.
 * Fetches workspaces from API and manages active workspace selection.
 */

import { useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/client/lib/api';
import {
  useWorkspaceStore,
  getActiveWorkspace,
  canPerformAction,
  type Workspace,
  type WorkspaceRole,
} from '@/client/stores/workspace-store';
import { useAuth } from './use-auth';

interface WorkspacesResponse {
  workspaces: Workspace[];
  activeWorkspaceId: string | null;
}

interface SwitchWorkspaceResponse {
  success: boolean;
  activeWorkspaceId: string;
}

/**
 * Fetch workspaces from API
 */
async function fetchWorkspaces(): Promise<WorkspacesResponse> {
  const response = await apiFetch('/api/workspaces');
  if (!response.ok) {
    throw new Error('Failed to fetch workspaces');
  }
  return response.json();
}

/**
 * Switch active workspace via API
 */
async function switchWorkspaceApi(workspaceId: string): Promise<SwitchWorkspaceResponse> {
  const response = await apiFetch(`/api/workspaces/${workspaceId}/switch`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to switch workspace');
  }
  return response.json();
}

/**
 * Hook for managing workspaces
 */
export function useWorkspace() {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const {
    activeWorkspaceId,
    workspaces,
    isLoading: storeLoading,
    setActiveWorkspace,
    setWorkspaces,
    setLoading,
    clearWorkspaces,
  } = useWorkspaceStore();

  // Fetch workspaces when authenticated
  const {
    data,
    isLoading: queryLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['workspaces'],
    queryFn: fetchWorkspaces,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update store when data changes
  useEffect(() => {
    if (data?.workspaces) {
      setWorkspaces(data.workspaces);
    }
  }, [data, setWorkspaces]);

  // Clear workspaces on logout
  useEffect(() => {
    if (!isAuthenticated) {
      clearWorkspaces();
    }
  }, [isAuthenticated, clearWorkspaces]);

  // Switch workspace mutation
  const switchMutation = useMutation({
    mutationFn: switchWorkspaceApi,
    onSuccess: (result) => {
      setActiveWorkspace(result.activeWorkspaceId);
      // Invalidate all workspace-scoped queries
      queryClient.invalidateQueries({ queryKey: ['links'] });
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
  });

  // Get active workspace object
  const activeWorkspace = getActiveWorkspace({
    activeWorkspaceId,
    workspaces,
    isLoading: storeLoading,
    setActiveWorkspace,
    setWorkspaces,
    setLoading,
    clearWorkspaces,
  });

  // Switch workspace handler
  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      if (workspaceId === activeWorkspaceId) return;
      await switchMutation.mutateAsync(workspaceId);
    },
    [activeWorkspaceId, switchMutation]
  );

  // Permission check for current workspace
  const can = useCallback(
    (action: 'view' | 'create' | 'edit' | 'delete' | 'invite' | 'manage_roles'): boolean => {
      if (!activeWorkspace) return false;
      return canPerformAction(activeWorkspace.role, action);
    },
    [activeWorkspace]
  );

  return {
    // State
    workspaces,
    activeWorkspace,
    activeWorkspaceId,
    isLoading: queryLoading || storeLoading,
    isSwitching: switchMutation.isPending,
    error,

    // Actions
    switchWorkspace,
    refetchWorkspaces: refetch,

    // Permissions
    can,
    role: activeWorkspace?.role ?? null,
    isOwner: activeWorkspace?.isOwner ?? false,

    // Convenience
    hasMultipleWorkspaces: workspaces.length > 1,
  };
}

export type { Workspace, WorkspaceRole };
